import { campaignApiDelete, campaignApiGet, campaignApiPost, campaignApiPatch } from '@/lib/api/campaign-api-client';
import type {
  ContactActivityPagedResponseEntityResponseApi,
  ContactEntityResponseApi,
  ContactSummaryPagedResponseEntityResponseApi,
  CreateContactRequestApi,
  DeleteContactsResponseApi,
  GetContactsRestParams,
  UpdateContactRequestApi,
  UpdateSubscriptionStatusRequestApi,
} from '../types/contacts-api.types';
import type { ContactActivityPagingResult } from '../types/contact-activity.types';
import type { Contact, ContactsPagingResult, InsertContactResult, UpdateContactResult } from '../types/contacts.types';
import {
  mapContactActivityRestResponse,
  mapContactApiToContact,
  mapContactsRestResponse,
} from '../utils/contact-api-mapper';
import { appendRestFilterQuery } from '../utils/contacts-rest-filter.utils';

const assertCampaignApiSuccess = <T>(
  response: { success?: boolean; message?: string | null; errors?: string[] | null; data?: T } | null | undefined,
  errorLabel: string
): T => {
  if (!response || typeof response !== 'object') {
    throw new Error(`${errorLabel}: invalid response structure`);
  }

  if (response.success === false) {
    const message =
      response.message ||
      (Array.isArray(response.errors) ? response.errors.join(', ') : undefined) ||
      errorLabel;
    throw new Error(message);
  }

  const data = response.data;
  if (!data || typeof data !== 'object') {
    throw new Error(`${errorLabel}: missing data payload`);
  }

  return data;
};

/**
 * `GET /v1/contacts` — the contacts table's single request: free-text `q`, the multi-value
 * status/list filters and `filter[...]` custom attributes all narrow the same result set.
 *
 * This replaced a two-function split where filtering went to `GET /v1/contacts/search` instead.
 * That endpoint is a deprecated alias whose contract is frozen and which never reports
 * `pageInfo.totalUnfiltered`, so the "(filtered from N)" footer was impossible to render through
 * it. `/v1/contacts` is a strict superset — same envelope, same `ContactSummary` items — and it is
 * the only contacts read this app uses now. `/v1/contacts/search` still exists server-side for
 * external API consumers (ticket #55); nothing in this repo should call it.
 */
export const getContactsFromRest = async (
  params: GetContactsRestParams = {}
): Promise<ContactsPagingResult> => {
  const pageNo = params.pageNo ?? 1;
  const pageSize = params.pageSize ?? 10;
  const offset = Math.max(0, (pageNo - 1) * pageSize);
  const q = params.q?.trim();

  const response = await campaignApiGet<ContactSummaryPagedResponseEntityResponseApi>(
    '/v1/contacts',
    appendRestFilterQuery(
      {
        ...(q ? { Q: q } : {}),
        ...(params.suppressed !== undefined
          ? { suppressed: params.suppressed ? 'true' : 'false' }
          : {}),
        Limit: pageSize,
        Offset: offset,
        Sort: params.sort,
        Order: params.order,
      },
      params.filter
    )
  );

  const data = assertCampaignApiSuccess(response, 'Failed to fetch contacts');
  return mapContactsRestResponse(data.items, data.pageInfo, { pageNo, pageSize });
};

export const getContactByIdFromRest = async (contactId: string): Promise<Contact> => {
  const id = contactId.trim();
  if (!id) {
    throw new Error('Contact id is required');
  }

  const response = await campaignApiGet<ContactEntityResponseApi>(`/v1/contacts/${encodeURIComponent(id)}`);
  const data = assertCampaignApiSuccess(response, 'Failed to fetch contact');
  return mapContactApiToContact(data);
};

export const createContactFromRest = async (
  input: CreateContactRequestApi
): Promise<InsertContactResult> => {
  const response = await campaignApiPost<ContactEntityResponseApi>('/v1/contacts', input);
  const data = assertCampaignApiSuccess(response, 'Failed to add contact');
  const itemId = data.itemId?.trim() ?? '';

  if (!itemId) {
    throw new Error(response.message || 'Failed to add contact');
  }

  return {
    acknowledged: true,
    itemId,
    totalImpactedData: 1,
    message: response.message ?? undefined,
    merged: data.merged === true,
  };
};

export const updateContactFromRest = async (
  contactId: string,
  input: UpdateContactRequestApi
): Promise<UpdateContactResult> => {
  const id = contactId.trim();
  if (!id) {
    throw new Error('Contact id is required');
  }

  const response = await campaignApiPatch<ContactEntityResponseApi>(
    `/v1/contacts/${encodeURIComponent(id)}`,
    input
  );
  const data = assertCampaignApiSuccess(response, 'Failed to update contact');
  const itemId = data.itemId?.trim() ?? id;

  return {
    acknowledged: true,
    itemId,
    totalImpactedData: 1,
    message: response.message ?? undefined,
  };
};

export const updateContactSubscriptionsFromRest = async (
  contactId: string,
  input: UpdateSubscriptionStatusRequestApi
): Promise<UpdateContactResult> => {
  const id = contactId.trim();
  if (!id) {
    throw new Error('Contact id is required');
  }

  const response = await campaignApiPatch<ContactEntityResponseApi>(
    `/v1/contacts/${encodeURIComponent(id)}/subscriptions`,
    input
  );
  const data = assertCampaignApiSuccess(response, 'Failed to update contact subscriptions');
  const itemId = data.itemId?.trim() ?? id;

  return {
    acknowledged: true,
    itemId,
    totalImpactedData: 1,
    message: response.message ?? undefined,
    contact: mapContactApiToContact(data),
  };
};

export const deleteContactFromRest = async (contactId: string): Promise<UpdateContactResult> => {
  const id = contactId.trim();
  if (!id) {
    throw new Error('Contact id is required');
  }

  const result = await deleteContactsFromRest([id]);

  return {
    acknowledged: true,
    itemId: id,
    totalImpactedData: result.totalImpactedData,
    message: result.message,
  };
};

export const deleteContactsFromRest = async (
  contactIds: string[]
): Promise<{ acknowledged: boolean; totalImpactedData: number; message?: string }> => {
  const ids = Array.from(new Set(contactIds.map((id) => id.trim()).filter(Boolean)));
  if (ids.length === 0) {
    throw new Error('Select at least one contact');
  }

  const response = await campaignApiDelete<DeleteContactsResponseApi>('/v1/contacts', {
    contactIds: ids,
  });

  if (!response || typeof response !== 'object') {
    throw new Error('Failed to delete contacts: invalid response structure');
  }

  if (response.success === false) {
    const message =
      response.message ||
      (Array.isArray(response.errors) ? response.errors.join(', ') : undefined) ||
      'Failed to delete contacts';
    throw new Error(message);
  }

  return {
    acknowledged: true,
    totalImpactedData: response.data?.deletedCount ?? ids.length,
    message: response.message ?? undefined,
  };
};

/**
 * `GET /v1/contacts/{id}/activity` — cursor-paged (#69): the first page has no `Cursor`, each
 * later one passes back the previous page's `pageInfo.nextCursor`. The endpoint ignores `Offset`,
 * so offset paging re-fetched the first page on every "Load more".
 */
export const getContactActivityFromRest = async (
  contactId: string,
  paging: { cursor?: string; pageSize?: number } = {}
): Promise<ContactActivityPagingResult> => {
  const id = contactId.trim();
  if (!id) {
    throw new Error('Contact id is required');
  }

  const response = await campaignApiGet<ContactActivityPagedResponseEntityResponseApi>(
    `/v1/contacts/${encodeURIComponent(id)}/activity`,
    {
      Limit: paging.pageSize ?? 10,
      Cursor: paging.cursor,
    }
  );

  const data = assertCampaignApiSuccess(response, 'Failed to fetch contact activity');
  return mapContactActivityRestResponse(data);
};
